import {
  BoundNumberField,
  Button,
  Chip,
  collapseColumn,
  column,
  Css,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTable,
  numericColumn,
  ScrollableContent,
  ScrollableFooter,
  ScrollableParent,
  simpleHeader,
  Switch,
  TabsWithContent,
  useModal,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, useFormState } from "@homebound/form-state";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useNavigate } from "react-router-dom";
import { Loading, Price, useNavigationCheck } from "src/components";
import { CollapseRowToggle } from "src/components/formFields/CollapseRowToggle";
import { NamedFragment } from "src/fragments/commonFragments.generated";
import { BidPackageStatus, SavePlanBasedBidContractLineItemInput } from "src/generated/graphqlTypes";
import { AddTradePartnerModal } from "src/routes/bids/components/AddTradePartnerModal";
import { pluralize, queryResult } from "src/utils";
import {
  PlanBasedBidsTableV2_BidContractRevisionFragment,
  PlanBasedBidsTableV2_BidPackageBidderFragment,
  PlanBasedBidsTableV2_BidPackageFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupLineFragment,
  PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment,
  PlanBasedBidsTableV2_BidPlanGroupFragment,
  PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment,
  PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment,
  usePlanBasedBidsTableV2_BidPlanChildGroupsQuery,
} from "./PlanBasedBidsTableV2.generated";
import { PlanBasedBidsUnmatchedScopeTable } from "./PlanBasedBidsUnmatchedScopeTable";

/** Holds each POF's data as the user opens its tab, so we don't have to load them all up front. */
type LoadedByPof = Record<string, readonly PlanBasedBidsTableV2_BidPlanGroupFragment[]>;

type PlanBasedBidsTableV2Props = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  searchFilter?: string;
  showDownloadButton?: boolean;
} & (
  | {
      mode?: "view";
      /** If set, narrow the table to just the matching BCR and its bidder. */
      bidContractRevision?: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment;
      onSave?: undefined;
    }
  | {
      mode: "edit";
      /** Required in edit mode: identifies the BCR (and bidder) whose costs are being edited. */
      bidContractRevision: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment;
      onSave: (input: SavePlanBasedBidContractLineItemInput[]) => void | Promise<void>;
    }
);

export function PlanBasedBidsTableV2(props: PlanBasedBidsTableV2Props) {
  const { bidPackage, searchFilter, bidContractRevision } = props;
  const mode = props.mode ?? "view";
  const sortedPofs = bidPackage.productOfferings.sortBy((pof) => pof.id);
  const navigate = useNavigate();
  const [selectedTab, setSelectedTab] = useState<string>(sortedPofs[0].id);
  const [showScope, setShowScope] = useState(false);
  const query = usePlanBasedBidsTableV2_BidPlanChildGroupsQuery({
    variables: { bidPackageId: bidPackage.id, productOfferingId: selectedTab, showScope },
  });

  // Single formState across all POF tabs. The table query still fires per active
  // tab, but each tab's `bidPlanGroups` are merged into `loadedByPof` and passed
  // as form-state's reactive `init.input`. form-state diffs the cost list by id,
  // so user edits survive when a new tab loads and the input grows.
  const [loadedByPof, setLoadedByPof] = useState<LoadedByPof>({});
  useEffect(() => {
    if (mode !== "edit") return;
    const data = query.data?.bidPackage.bidPlanGroups;
    const pofId = query.variables?.productOfferingId;
    if (!data || !pofId) return;
    setLoadedByPof((prev) => (prev[pofId] === data ? prev : { ...prev, [pofId]: data }));
  }, [mode, query]);

  const formState = useFormState({
    readOnly: mode !== "edit",
    config: editFormConfig,
    init: {
      input: loadedByPof,
      map: (loaded) => buildEditCosts(loaded, bidContractRevision?.id, bidPackage.costCodes),
    },
  });

  // Warn before leaving the page with unsaved costs.
  const { useRegisterNavigationCheck } = useNavigationCheck();
  useRegisterNavigationCheck(() => mode !== "edit" || !formState.dirty, [mode, formState]);

  async function handleSave() {
    // Filter to dirty rows (a row is dirty if the user changed totalCostInCents.
    // Read full row.value rather than `changedValue` because we always
    // need groupLineId/costCodeId on the wire, not just dirty fields.
    const input = formState.costs.rows
      .filter((r) => r.dirty)
      .map((r) => ({
        id: r.value.pbbcliId,
        totalCostInCents: r.value.totalCostInCents,
        costCodeId: r.value.costCodeId,
        groupLineId: r.value.groupLineId,
      }));
    await props.onSave?.(input);
    formState.commitChanges();
  }

  return (
    <>
      <ScrollableParent>
        <ScrollableContent virtualized>
          <TabsWithContent
            alwaysShowAllTabs
            right={<Switch label="Show Scope" selected={showScope} onChange={setShowScope} compact />}
            tabs={sortedPofs.map((pof) => ({
              name: pof.name,
              value: pof.id,
              render: () => {
                if (query.loading) return <Loading />;
                return queryResult(query, ({ bidPackage: { bidPlanGroups, scopedLineItems } }) => {
                  const sortedBpgs = bidPlanGroups.sortByKey("order");
                  const scoped = scopedLineItems ?? [];
                  return (
                    <>
                      {showScope && <PlanBasedBidsUnmatchedScopeTable scopedLineItems={scoped} />}
                      {sortedBpgs.flatMap((bpg) =>
                        bpg.childGroups.map((bpcg) => (
                          <div css={Css.mb2.$} key={bpcg.id}>
                            <PlanBasedBidsTableView
                              bidPackage={bidPackage}
                              bidContractRevision={bidContractRevision}
                              bidPlanGroup={bpg}
                              childGroup={bpcg}
                              productOffering={pof}
                              searchFilter={searchFilter}
                              mode={mode}
                              formState={mode === "edit" ? formState : undefined}
                              showScope={showScope}
                              scopedLineItems={scoped}
                            />
                          </div>
                        )),
                      )}
                    </>
                  );
                });
              },
            }))}
            selected={selectedTab}
            onChange={setSelectedTab}
          />
        </ScrollableContent>
      </ScrollableParent>
      {mode === "edit" && (
        <ScrollableFooter>
          <div css={Css.df.px6.py3.bshHover.bgWhite.$}>
            <div css={Css.mla.df.gap2.$}>
              <Button size="lg" onClick={() => navigate(-1)} label="Cancel" variant="quaternary" />
              <Button size="lg" onClick={handleSave} label="Save" />
            </div>
          </div>
        </ScrollableFooter>
      )}
    </>
  );
}

type PlanBasedBidsTableViewProps = {
  bidPackage: PlanBasedBidsTableV2_BidPackageFragment;
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
  childGroup: PlanBasedBidsTableV2_BidPlanChildGroupFragment;
  productOffering: NamedFragment;
  searchFilter?: string;
  mode: "view" | "edit";
  formState: ObjectState<EditFormValues> | undefined;
  bidContractRevision: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment | undefined;
  showScope: boolean;
  scopedLineItems: PlanBasedBidsTableV2_BidPlanChildGroupScopedLineFragment[];
};

function PlanBasedBidsTableView(props: PlanBasedBidsTableViewProps) {
  const {
    bidPackage,
    bidPlanGroup,
    childGroup,
    productOffering,
    searchFilter,
    mode,
    formState,
    bidContractRevision,
    showScope,
    scopedLineItems,
  } = props;
  const { openModal } = useModal();
  const activeBidders = bidPackage.activeBidders;
  const bcrs = useMemo(
    () => (bidContractRevision ? [bidContractRevision] : bidPackage.latestVersion.latestBidContractRevisions),
    [bidContractRevision, bidPackage],
  );
  const hasTrades = activeBidders.nonEmpty;
  // Hide the inline Add Trade link in the create flow (status === Draft) — the bid
  // package isn't open for bidding yet, so adding a trade isn't actionable.
  const canAddTrade = mode === "view" && !hasTrades && bidPackage.status.code !== BidPackageStatus.Draft;

  // When narrowed to a specific BCR, render only the bidder whose BC matches that BCR.
  const bidders = useMemo(
    () =>
      bidContractRevision
        ? activeBidders.filter((b) => b.bidContract?.id === bidContractRevision.bidContract.id)
        : activeBidders,
    [activeBidders, bidContractRevision],
  );

  const costRowLookup = useCallback(
    (bpglId: string, costCodeId: string) =>
      formState?.costs.rows.find((r) => r.value.groupLineId === bpglId && r.value.costCodeId === costCodeId),
    [formState],
  );

  const scopedLineItemsByGroupLineId = useMemo(
    () =>
      scopedLineItems.keyBy(
        (sli) => sli.groupLine?.id ?? sli.id,
        (sli) => sli.takeoffLineItems,
      ),
    [scopedLineItems],
  );

  // In view mode, when narrowed to a specific BCR (e.g. viewing an older signed version),
  // only render lines that had a cost saved on that revision. Lines added in a later
  // version shouldn't show up in the historical view. In edit mode we keep every line so
  // the user can enter costs against any of them.
  const rowFilterBcr = mode === "view" ? bidContractRevision : undefined;
  const rows = useMemo(
    () => createRows(bidPlanGroup, childGroup, rowFilterBcr, showScope ? scopedLineItemsByGroupLineId : undefined),
    [bidPlanGroup, childGroup, rowFilterBcr, showScope, scopedLineItemsByGroupLineId],
  );
  const columns = useMemo(
    () =>
      createColumns({
        bidPlanGroup,
        productOffering,
        activeBidders: bidders,
        bidContractRevisions: bcrs,
        costCodes: bidPackage.costCodes,
        addTradeAction: canAddTrade
          ? () => openModal({ content: <AddTradePartnerModal bidPackage={bidPackage} /> })
          : undefined,
        mode,
        costRowLookup,
        showScope,
        scopedLineItemsByGroupLineId,
      }),
    [
      bidPlanGroup,
      productOffering,
      bidders,
      bcrs,
      bidPackage,
      canAddTrade,
      openModal,
      mode,
      costRowLookup,
      showScope,
      scopedLineItemsByGroupLineId,
    ],
  );
  return (
    <div>
      <TableHeader bidPlanGroup={bidPlanGroup} childGroup={childGroup} />
      <GridTable columns={columns} filter={searchFilter} rows={rows} id={childGroup.id} />
    </div>
  );
}

type HeaderRow = { kind: "header"; data: undefined };
// Display the first dimension's option name (e.g. "Scheme A") as the parent of the cost lines.
type ParentRow = { kind: "parent"; data: { name: string } };
type CostLineRow = {
  kind: "costLine";
  data: { bidPlanChildGroupLine: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment; display: Record<string, string> };
};
// One row per takeoff line item matched to a cost line, rendered nested below its cost line
// when the Show Scope toggle is on.
type ScopeLineRow = { kind: "scopeLine"; data: { displayName: string } };
type Row = HeaderRow | ParentRow | CostLineRow | ScopeLineRow;

// Layout: collapse + dimension columns + flex spacer + (per-bidder × cost-code columns | Add Trade link).
// The bidder name is stacked above the cost code in the first cost-code column of each bidder;
// subsequent columns show only the cost code. Cell costs come from PB-BCLIs joined to the bidder's
// BCR (matched by bidContract.id), so an unsubmitted request renders empty cells.
function createColumns(props: {
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment;
  productOffering: NamedFragment;
  activeBidders: PlanBasedBidsTableV2_BidPackageBidderFragment[];
  bidContractRevisions: PlanBasedBidsTableV2_BidContractRevisionFragment[];
  costCodes: NamedFragment[];
  addTradeAction: (() => void) | undefined;
  mode: "view" | "edit";
  costRowLookup: (bpglId: string, costCodeId: string) => ObjectState<CostInput> | undefined;
  showScope: boolean;
  scopedLineItemsByGroupLineId: Record<string, PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment[]>;
}): GridColumn<Row>[] {
  const {
    bidPlanGroup,
    productOffering,
    activeBidders,
    bidContractRevisions,
    costCodes,
    addTradeAction,
    mode,
    costRowLookup,
    showScope,
    scopedLineItemsByGroupLineId,
  } = props;
  const hasTrades = activeBidders.nonEmpty;

  // Fixed widths so dimension columns stay tight to the left; a flex spacer below absorbs
  // any leftover horizontal space and pushes trade columns to the right edge.
  // When the group has no dimensions the backend emits a single options=[] line so the
  // group renders as one row labeled with the product offering's name (a "base only" cost).
  // The first dimension column is also used to render the scope line item names (nested under
  // their cost line) so the takeoff name lines up with the dimension's value above.
  const dimensionColumns = bidPlanGroup.dimensions.isEmpty
    ? [
        column<Row>({
          header: "Plan",
          parent: emptyCell,
          costLine: productOffering.name,
          scopeLine: (tli) => tli.displayName,
          w: "300px",
        }),
      ]
    : bidPlanGroup.dimensions.map((dim, idx) =>
        column<Row>({
          header: dim.globalOptionType.name,
          // The first dimension column is the parent row label for the group rows.
          parent: idx === 0 ? ({ name }) => name : emptyCell,
          costLine: ({ display }) => display[dim.globalOptionType.id] ?? emptyCell,
          // Show the takeoff line item's name in the first dimension column; other dimension
          // columns are empty so the scope row reads as a single label under its cost line.
          scopeLine: idx === 0 ? (tli) => tli.displayName : emptyCell,
          w: "300px",
        }),
      );

  const spacerColumn = column<Row>({
    header: emptyCell,
    parent: emptyCell,
    costLine: emptyCell,
    scopeLine: emptyCell,
    w: 1,
  });

  const scopedLineColumn = showScope
    ? [
        column<Row>({
          header: "Scope",
          parent: emptyCell,
          costLine: ({ bidPlanChildGroupLine }, { row, api }) => {
            const count = scopedLineItemsByGroupLineId[bidPlanChildGroupLine.id]?.length ?? 0;
            if (count === 0) return "0 lines";
            return (
              <CollapseRowToggle api={api} rowId={row.id} label={`${count} ${pluralize(count, "line")}`} compact />
            );
          },
          scopeLine: emptyCell,
          w: "150px",
        }),
      ]
    : [];

  // Default collapseColumn renders a chevron on rows that have children; scope rows are
  // leaves (no further children), so we render an empty cell to suppress the indentation marker.
  const collapseCol = collapseColumn<Row>({ scopeLine: emptyCell });

  if (!hasTrades) {
    // Once any request exists this column disappears in favor of the (request × costCode) leaf columns below
    return [
      collapseCol,
      ...dimensionColumns,
      spacerColumn,
      column<Row>({
        header: () =>
          addTradeAction ? <Button label="Add Trade" variant="text" onClick={addTradeAction} /> : emptyCell,
        parent: emptyCell,
        costLine: emptyCell,
        scopeLine: emptyCell,
        w: "300px",
      }),
      ...scopedLineColumn,
    ];
  }

  const tradeColumns = activeBidders.flatMap((bidder) => {
    const bcr = bidContractRevisions.find((b) => b.bidContract.id === bidder.bidContract?.id);
    return costCodes.map((cc, idx) => {
      const isFirstCostCodeForTrade = idx === 0;
      return numericColumn<Row>({
        // First cost-code column for each bidder stacks the bidder name above the cost-code label;
        // subsequent columns render an invisible placeholder in the bidder-name slot so the
        // cost-code labels stay aligned across all bidder columns.
        header: {
          content: (
            <div css={Css.df.fdc.w100.$}>
              <span css={Css.smSb.gray900.lineClamp1.if(!isFirstCostCodeForTrade).vh.$}>
                {isFirstCostCodeForTrade ? bidder.name : "."}
              </span>
              <span css={Css.xsSb.gray700.$}>{cc.name}</span>
            </div>
          ),
          alignment: "left",
        },
        parent: emptyCell,
        costLine: ({ bidPlanChildGroupLine }) => {
          if (mode === "edit") {
            const row = costRowLookup(bidPlanChildGroupLine.id, cc.id);
            if (!row) return emptyCell;
            return { content: <BoundNumberField field={row.totalCostInCents} type="cents" />, alignment: "left" };
          }
          if (!bcr) return emptyCell;
          const pbbcli = bidPlanChildGroupLine.planBasedBidContractLineItems.find(
            (pbbcli) => pbbcli.revision.id === bcr.id && pbbcli.costCode.id === cc.id,
          );
          return { content: <Price valueInCents={pbbcli?.totalCostInCents} />, alignment: "left" };
        },
        scopeLine: emptyCell,
        w: "300px",
      });
    });
  });

  return [collapseCol, ...dimensionColumns, spacerColumn, ...tradeColumns, ...scopedLineColumn];
}

// Simple header followed by one collapsible parent row per first-dimension option (e.g. Elevation).
// The parent row is just a label; its cost lines nest as children.
// When `bcr` is passed, lines are derived from BCR -> PB-BCLI -> ChildLine so a historical
// version only renders lines that actually had a cost on that revision. Lines archived from
// the BP after this revision was signed still render via the BCR's PB-BCLIs.
function createRows(
  bidPlanGroup: PlanBasedBidsTableV2_BidPlanGroupFragment,
  childGroup: PlanBasedBidsTableV2_BidPlanChildGroupFragment,
  bcr: PlanBasedBidsTableV2_NarrowedBidContractRevisionFragment | undefined,
  scopedLineItemsByGroupLineId: Record<string, PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment[]> | undefined,
): GridDataRow<Row>[] {
  const lines = bcr
    ? bcr.planBasedLineItems
        .filter((pbbcli) => pbbcli.groupLine.group.id === childGroup.id)
        .map((pbbcli) => pbbcli.groupLine)
        .uniqueByKey("id")
    : childGroup.lines;
  const firstDimensionTypeId = bidPlanGroup.dimensions[0]?.globalOptionType.id;

  // No dimensions: the group is a single "base only" line, so render it flat under the header.
  if (!firstDimensionTypeId) {
    return [simpleHeader, ...buildCostLineRows(lines, scopedLineItemsByGroupLineId)];
  }

  // Otherwise group the lines by their first-dimension option and nest each group under a parent.
  const groups = lines.groupByObject(
    (bpcgl) => bpcgl.options.find((o) => o.globalOption.type.id === firstDimensionTypeId)?.globalOption,
  );
  const parentRows = groups.map(([parentGo, bpcgls]) => ({
    kind: "parent" as const,
    id: `parent:${parentGo?.id}`,
    data: { name: parentGo?.name ?? "" },
    children: buildCostLineRows(bpcgls, scopedLineItemsByGroupLineId),
  }));

  return [simpleHeader, ...parentRows];
}

// Build the cost line row for each child group line.
function buildCostLineRows(
  bpcgls: PlanBasedBidsTableV2_BidPlanChildGroupLineFragment[],
  scopedLineItemsByGroupLineId: Record<string, PlanBasedBidsTableV2_ScopeTakeoffLineItemFragment[]> | undefined,
): GridDataRow<Row>[] {
  return bpcgls.map((bpcgl) => {
    const display = bpcgl.options.mapToObject((rpo) => [rpo.globalOption.type.id, rpo.globalOption.name]);
    return {
      kind: "costLine",
      id: bpcgl.id,
      data: { bidPlanChildGroupLine: bpcgl, display },
      children: scopedLineItemsByGroupLineId?.[bpcgl.id]?.map((tli) => ({
        kind: "scopeLine",
        id: `${bpcgl.id}:${tli.id}`,
        data: { displayName: `${tli.item.fullCode} ${tli.name}` },
      })),
      initCollapsed: true,
    };
  });
}

function TableHeader(props: Pick<PlanBasedBidsTableViewProps, "bidPlanGroup" | "childGroup">) {
  const { bidPlanGroup, childGroup } = props;
  // Count distinct globalOption IDs per dimension type from the current POF's child group lines
  const countsByDimensionType = useMemo(() => {
    const allOptions = bidPlanGroup.childGroups.flatMap((cg) => cg.lines.flatMap((line) => line.options));
    const grouped = allOptions.groupBy((opt) => opt.globalOption.type.id);
    return grouped
      .toEntries()
      .mapToObject(([typeId, opts]) => [typeId, opts.map((o) => o.globalOption.id).unique().length]);
  }, [bidPlanGroup]);

  return (
    <div css={Css.df.aic.gap2.px2.py1.bgWhite.bb.bcGray200.$}>
      <span data-testid="tableHeader" css={Css.mdSb.gray900.$}>
        {childGroup.displayName}
      </span>
      {bidPlanGroup.dimensions.map((dim) => {
        const count = countsByDimensionType[dim.globalOptionType.id] ?? 0;
        return <Chip key={dim.id} text={`${count} ${pluralize(count, dim.globalOptionType.name)}`} />;
      })}
    </div>
  );
}

type CostInput = {
  id: string | undefined;
  pbbcliId: string | undefined;
  groupLineId: string;
  costCodeId: string;
  totalCostInCents: number | null | undefined;
};

type EditFormValues = { costs: CostInput[] };

const editFormConfig: ObjectConfig<EditFormValues> = {
  costs: {
    type: "list",
    // update: incremental since we want only the rows the user touched on changedValue
    update: "incremental",
    config: {
      id: { type: "value" },
      pbbcliId: { type: "value" },
      groupLineId: { type: "value" },
      costCodeId: { type: "value" },
      totalCostInCents: { type: "value" },
    },
  },
};

// Build the cost list (one per BPGL × CostCode) from every PO's loaded bidPlanGroups.
// Existing PB-BCLIs (matched by `(bcr.id, costCode.id)`) seed their `pbbcliId` and current
// `totalCostInCents`; missing ones get a synthetic id so form-state can still match the
// row across re-inits when more tabs load.
function buildEditCosts(
  loaded: LoadedByPof,
  bcrId: string | undefined,
  costCodes: readonly NamedFragment[],
): EditFormValues {
  const bpcgls = loaded
    .toValues()
    .flatMap((bidPlanGroups) => bidPlanGroups.flatMap((bpg) => bpg.childGroups))
    .flatMap((bpcg) => bpcg.lines);

  const costs = bpcgls.flatMap((bpcgl) =>
    costCodes.map((cc) => {
      const pbbcli = bcrId
        ? bpcgl.planBasedBidContractLineItems.find((p) => p.revision.id === bcrId && p.costCode.id === cc.id)
        : undefined;
      return {
        id: pbbcli?.id ?? `${bpcgl.id}:${cc.id}`,
        pbbcliId: pbbcli?.id,
        groupLineId: bpcgl.id,
        costCodeId: cc.id,
        totalCostInCents: pbbcli?.totalCostInCents,
      };
    }),
  );
  return { costs };
}
